import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form, Input, Select } from 'antd';
import ConfirmModal from 'src/components/confirm-modal';
import './style.scss';

/**
 * FormInactivateModal component - Modal for inactivating a profile following Figma flow.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.visible - Indicates if the modal is visible.
 * @param {Function} props.onCancel - The function to close the modal.
 * @param {Function} props.onConfirm - The function to confirm the inactivation.
 * @param {Object} props.collaborator - The collaborator object to be inactivated.
 * @param {Boolean} props.loading - Indicates if the form is in a loading state.
 * @returns {JSX.Element} The rendered FormInactivateModal component.
 */
function FormInactivateModal({ visible, onCancel, onConfirm, collaborator, loading }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [currentStep, setCurrentStep] = useState('form');
  const [isFormValid, setIsFormValid] = useState(false);
  const [showOtherReasonField, setShowOtherReasonField] = useState(false);
  const [confirmedValues, setConfirmedValues] = useState({});

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setCurrentStep('form');
      setIsFormValid(false);
      setShowOtherReasonField(false);
    }
  }, [visible, form]);

  const handleFormChange = (changedValues, allValues) => {
    if (changedValues.reason !== undefined) {
      const shouldShowOtherField = changedValues.reason === 'OTHER';
      setShowOtherReasonField(shouldShowOtherField);
      if (!shouldShowOtherField) {
        form.setFieldsValue({ otherReason: '' });
      }
    }

    const days = allValues?.days?.toString().trim() || '';
    const reason = allValues?.reason || '';
    const otherReason = allValues?.otherReason?.trim() || '';
    const isDaysValid = days !== '' && !isNaN(Number(days)) && Number(days) >= 1 && Number(days) <= 30;
    const isOtherReasonValid = reason !== 'OTHER' || (otherReason !== '' && otherReason.length <= 36);
    setIsFormValid(isDaysValid && reason !== '' && isOtherReasonValid);
  };

  const handleContinue = () => {
    form.validateFields().then((values) => {
      setConfirmedValues(values);
      setCurrentStep('confirmation');
    }).catch((errorInfo) => {
      console.log('Validation failed:', errorInfo);
    });
  };

  const handleBackToForm = () => {
    setCurrentStep('form');
  };

  const handleFinalConfirm = () => {
    onConfirm({
      ...confirmedValues,
      collaboratorId: collaborator?.id,
      login: collaborator?.collaborator_deallocation?.login
    });
  };

  const reasonOptions = [
    { value: 'ON_NOTICE', label: t('inactivateModal.reasons.onNotice') },
    { value: 'CONTRACT_RENEWAL', label: t('inactivateModal.reasons.contractRenewal') },
    { value: 'LEAVE', label: t('inactivateModal.reasons.leave') },
    { value: 'OTHER', label: t('inactivateModal.reasons.other') }
  ];

  const renderFormStep = () => (
    <ConfirmModal
      title={t('inactivateProfile')}
      visible={visible}
      onCancel={onCancel}
      onConfirm={handleContinue}
      okText={t('continue')}
      cancelText={t('cancel')}
      confirmLoading={loading}
      okButtonProps={{
        disabled: !isFormValid,
        className: `cit-btn ${isFormValid ? 'primary-type enabled' : 'disabled-type'}`,
      }}
      className="form-inactivate-modal"
    >
      <div className="form-inactivate-modal__content">
        <p className="form-inactivate-modal__description">
          {t('inactivateModal.description')}
        </p>
        
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormChange}
        >
          <Form.Item
            name="days"
            label={
              <span>
                {t('inactivateModal.daysLabel')}
                <span className="required-asterisk">*</span>
              </span>
            }
            required={false}
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.toString().trim() === '') {
                    return Promise.reject(t('inactivateModal.daysRequired'));
                  }
                  const numValue = Number(value);
                  if (isNaN(numValue) || numValue < 1 || numValue > 30) {
                    return Promise.reject(t('inactivateModal.daysRange'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              placeholder={t('inactivateModal.daysPlaceholder')}
              className="form-inactivate-modal__input"
              type="text"
              maxLength={2}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label={
              <span>
                {t('inactivateModal.reasonLabel')}
                <span className="required-asterisk">*</span>
              </span>
            }
            required={false}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(t('inactivateModal.reasonRequired'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Select
              placeholder={t('inactivateModal.reasonPlaceholder')}
              options={reasonOptions}
              className="form-inactivate-modal__select"
            />
          </Form.Item>

          {/* Campo dinâmico para "Outro motivo" */}
          {showOtherReasonField && (
            <Form.Item
              name="otherReason"
              label={t('inactivateModal.otherReasonLabel')}
              required={false}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.reject(t('inactivateModal.otherReasonRequired'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
              className="form-inactivate-modal__other-reason"
            >
              <Input.TextArea
                placeholder={t('inactivateModal.otherReasonPlaceholder')}
                className="form-inactivate-modal__textarea"
                rows={4}
                maxLength={36}
              />
            </Form.Item>
          )}
        </Form>
      </div>
    </ConfirmModal>
  );

  const renderConfirmationStep = () => {
    const collaboratorLogin = collaborator?.collaborator_deallocation?.login || '';
    const days = parseInt(confirmedValues.days, 10);

    return (
      <ConfirmModal
        title={t('confirmation')}
        visible={visible}
        onCancel={handleBackToForm}
        onConfirm={handleFinalConfirm}
        okText={t('confirm')}
        cancelText={t('cancel')}
        confirmLoading={loading}
        okButtonProps={{
          className: 'cit-btn primary-type enabled',
          style: {
            backgroundColor: '#117a6e',
            borderColor: '#117a6e',
            color: '#fff'
          }
        }}
        className="form-inactivate-modal confirmation-modal"
      >
        <div className="confirmation-modal__content">
          <p className="confirmation-modal__question"
            dangerouslySetInnerHTML={{ __html: t('inactivateModal.question', { login: collaboratorLogin }) }}
          />
          <p className="confirmation-modal__reminder"
            dangerouslySetInnerHTML={{ __html: t('inactivateModal.reminder', { days }) }}
          />
        </div>
      </ConfirmModal>
    );
  };

  return currentStep === 'form' ? renderFormStep() : renderConfirmationStep();
}

FormInactivateModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  collaborator: PropTypes.object,
  loading: PropTypes.bool
};

export default FormInactivateModal;